import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IoCallOutline, IoChatbubbleOutline } from "react-icons/io5";
import { CiVideoOn } from "react-icons/ci";
import { getSocket, SocketDisconnect } from '../../../socket';
import { Avatar, Box } from '@mui/material';
import Cookie from '../../../utils/Cookie';
import toast from 'react-hot-toast';
import { GET_CHAT_MESSAGES, GET_CHAT_STARTERS, GET_CREATOR_DETAILS, GET_MESSAGE_LIMITS, GET_PUBLIC_PROFILE_URL } from '../../../utils/apiUrl';
import { CallWithAuth, CallWithOutAuth } from '../../../actions/apiAction';
import { getAnonToken } from '../../../utils/anonymousSession';
import { trackEvent } from '../../../utils/analytics';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import SimplifiedAuthCard from '../../../components/Auth/SimplifiedAuthCard';
import { utcToLocal } from '../../../utils/helpers';
import { getDateLabel } from '../../../helpers/common';
import { useSelector } from 'react-redux';
import { unlockAudioContext, preloadAudio } from '../../../utils/audioContext';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { IoVideocamOutline } from "react-icons/io5";
import UpgradeSubscriptionModal from '../../../components/Modals/UpgradeSubscriptionModal';
import PayrollModal from '../../../components/Modals/ShowPayrollModal';
import useGlobalStore from '../../../hooks/useGlobalStore';
import { AnimatePresence, motion } from "framer-motion";
import EmojiPicker from "emoji-picker-react";
import AddIcon from '@mui/icons-material/Add';
import { useJoyride } from 'react-joyride';
import PwaInstallTip from '../../../components/PwaInstallTip';
import { GoDotFill } from "react-icons/go";
import VideoModal from '../../../components/Modals/OnboardingModal';
import useAuth from '../../../hooks/useAuth';
import { isPwaInstalled, getInstallPlatform, isPwaTipDismissed, dismissPwaTip, isSplashShownLocally, markSplashShownLocally } from '../../../utils/pwaDetection';
import './ChatPage.scss';



const steps = [
    {
        target: '[data-step="1"]',
        content: "You can start a video call by clicking here.",
        placement: "bottom"
    },
    {
        target: '[data-step="2"]',
        content: "Click here to start a voice call 📞",
        placement: "bottom"
    }
];
const CustomTooltip = ({
    step,
    primaryProps,
    skipProps,
    tooltipProps
}) => {
    return (
        <div
            {...tooltipProps}
            className="relative bg-[#1c1c22] text-white p-4 rounded-2xl shadow-xl max-w-[220px]"
        >
            {/* Content */}
            <div className="space-y-1 text-white">
                {step.content}
            </div>

            {/* Buttons */}
            <div className="flex justify-between mt-3 text-xs">
                <button {...skipProps} className="text-gray-400">
                    Skip
                </button>
                <button
                    {...primaryProps}
                    className="bg-[#FF4DA6] px-3 py-1 rounded-full"
                >
                    Next
                </button>
            </div>
        </div>
    );
};
const PinkBeacon = ({ onClick }) => {
    return (
        <span
            onClick={onClick}
            className="relative flex items-center justify-center w-5 h-5"
        >
            {/* Pulse ring */}
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF4DA6] opacity-75 animate-ping"></span>

            {/* Center dot */}
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FF4DA6]"></span>
        </span>
    );
};
const Chat = () => {
    const { TOGGLE_UPGRADE_SUBSCRIPTION, toggleForModal } = useGlobalStore();
    const { controls, failures, on, Tour } = useJoyride({
        continuous: true,
        steps,
        beaconComponent: PinkBeacon,
        styles: {
            options: {
                backgroundColor: "#1c1c22",
                primaryColor: "#FF4DA6",
                textColor: "#1c1c22",
                overlayColor: "rgba(0,0,0,0.7)",
            },
            buttonPrimary: {
                color: '#ffffff'
            }
        },
        tooltipComponent: (props) => <CustomTooltip {...props} />
    });
    const { isInitialized } = useSelector(state => state.socketSlice);
    const { user, updateUser, refreshUser } = useAuth();
    const { isSignedIn } = useClerkAuth();
    const navigator = useNavigate();
    const { search } = useLocation();
    const [creatorDetails, setUserDetails] = useState({});
    const query = new URLSearchParams(search);
    const creatorId = query.get("id");
    const creatorUsername = query.get("username") || "";
    const isAnonymous = !isSignedIn && Boolean(getAnonToken());
    const [showAccountPrompt, setShowAccountPrompt] = useState(false);
    const showPwaTipParam = query.get("show_pwa_tip") === "1";
    const [ChatId, setChatId] = useState(() => Cookie.getCookie('activeChatId'));
    const [message, setMessage] = useState('');
    const [messageList, setMessageList] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [rememberAnchorId, setRememberAnchorId] = useState(null);
    const rememberPinnedRef = useRef(false);
    const rememberChatIdRef = useRef(ChatId);
    const [starterSuggestions, setStarterSuggestions] = useState([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [aiTyping, setAiTyping] = useState(false);
    const [useLimit, setUseLimit] = useState({})
    const [openFrom, setOpenFrom] = useState('')
    const [subscriptionLimit, setSubscriptionLimit] = useState(null)
    const [loaderLimit, setLoaderLimit] = useState(false)
    const chatRootRef = useRef(null);
    const scrollContainerRef = useRef(null); // outer overflow-y-auto element (actual scroll container)
    const bottomRef = useRef(null); // anchor inside the message list
    const isPaginatingRef = useRef(false); // true while loading older messages
    const lastMessageIdRef = useRef(null); // last-message id to detect appends
    const isMounted = useRef(true);
    const userMessageLengthRef = useRef(0); // store user message length for reading delay
    const typingTimeoutRef = useRef(null); // cleanup timeout on unmount
    const aiResponseQueueRef = useRef(null); // queue AI response if it arrives early
    const isTypingShowingRef = useRef(false); // sync flag for typing animation state
    const dateSepElements = useRef({}); // { 'Today': HTMLElement, 'Yesterday': HTMLElement, ... }
    const floatingChipRef = useRef(null);     // direct DOM ref for imperative style updates
    const floatingChipTextRef = useRef(null); // direct DOM ref for text content
    const floatingLabelRef = useRef(null);    // tracks current label without triggering re-renders
    const scrollFadeTimerRef = useRef(null);  // timer to hide scrollbar after scrolling stops
    const touchStartY = useRef(null);
    const touchEndY = useRef(null);
    const swipeActivated = useRef(false);
    const [reactionMsg, setReactionMsg] = useState(null);
    const [reactionPosition, setReactionPosition] = useState({ x: 0, y: 0 });
    const longPressTimer = useRef(null);
    const [invisibleMode, setInvisibleMode] = useState(false);
    const [messageReactions, setMessageReactions] = useState({});
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showPwaInstallTip, setShowPwaInstallTip] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingChecked, setOnboardingChecked] = useState(true);
    const splashSyncedRef = useRef(false);
    const [splashApiLoading, setSplashApiLoading] = useState(false);
    const greetingEmittedRef = useRef(false);
    const isLoadingMessagesRef = useRef(false);

    const activateInvisibleMode = () => {
        setInvisibleMode(true);
        setTimeout(() => setInvisibleMode(false), 3000);
    };

    const startLongPress = (e, msg) => {
        e.stopPropagation();

        const point = e.touches ? e.touches[0] : e;


        longPressTimer.current = setTimeout(() => {
            navigator.vibrate?.(10);

            setReactionMsg(msg);
            setReactionPosition({
                x: point.clientX,
                y: point.clientY
            });
        }, 400);
    };

    const endLongPress = () => {
        clearTimeout(longPressTimer.current);
    };

    const getUserDetails = async () => {
        if (getAnonToken() && creatorUsername) {
            const response = await CallWithOutAuth("GET", GET_PUBLIC_PROFILE_URL(creatorUsername));
            if (response?.data) setUserDetails(response.data);
            return;
        }
        const response = await CallWithAuth("GET", GET_CREATOR_DETAILS(creatorId));
        if (response?.data) setUserDetails(response.data);
    };
    const getLimitsOfMessages = async () => {
        setLoaderLimit(true)
        const response = await CallWithAuth("GET", GET_MESSAGE_LIMITS(creatorId));
        if (response?.data) {
            setSubscriptionLimit(response.data)
        }
        setLoaderLimit(false)
    };

    const formatMessageCount = (value) => {
        const count = Number(value);
        if (Number.isNaN(count)) return '0';
        return String(Math.max(0, Math.floor(count)));
    };

    const formatMinutesToClock = (value) => {
        const minutes = Number(value);
        if (Number.isNaN(minutes)) return '00:00';

        const totalSeconds = Math.max(0, Math.round(minutes * 60));
        const wholeMinutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        return `${String(wholeMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const getUserList = async (beforeCursor = null) => {
        const socket = getSocket();
        if (!socket) {
            console.warn("⛔ getUserList — socket missing");
            return 0;
        }
        if (!ChatId) {
            console.warn("⛔ getUserList — chatId missing");
            return 0;
        }
        if (!beforeCursor && isLoadingMessagesRef.current) {
            console.warn("⛔ getUserList — already loading, skipped");
            return 0;
        }
        if (!socket) return;
        try {
            if (!beforeCursor) isLoadingMessagesRef.current = true;
            setLoadingMore(true);
            const url = beforeCursor ? `${GET_CHAT_MESSAGES(ChatId)}?before=${beforeCursor}` : GET_CHAT_MESSAGES(ChatId);
            const response = await CallWithAuth("GET", url);
            if (response?.data) {
                const newMessages = response.data.data || [];

                if (beforeCursor) {
                    setMessageList(prev => [...newMessages, ...prev]);
                } else {
                    if (newMessages.length === 0 && !greetingEmittedRef.current) {
                        greetingEmittedRef.current = true;
                        socket.emit('get_message', {
                            creator_id: creatorId,
                            chat_id: ChatId,
                            scenario: 'onboarding'
                        });
                        isTypingShowingRef.current = true;
                        setAiTyping(true)
                    }

                    setMessageList(newMessages);
                }
                return newMessages.length;
            }
            return 0;
        } catch (err) {
            console.error('Error fetching messages:', err);
            return 0;
        } finally {
            if (!beforeCursor) {
                isLoadingMessagesRef.current = false;
                setHistoryLoaded(true);
            }
            setLoadingMore(false);
        }
    };
    // load user details
    useEffect(() => {
        if (creatorId == 'undefined') {
            navigator('/creator-not-found/creator-not-found');
            return
        }
        if (creatorId) getUserDetails();
        if (creatorId) getLimitsOfMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatorId]);

    useEffect(() => {
        let cancelled = false;
        CallWithAuth("GET", GET_CHAT_STARTERS).then((response) => {
            const starters = response?.data?.starters;
            if (!cancelled && Array.isArray(starters)) {
                setStarterSuggestions(starters);
            }
        });
        return () => { cancelled = true; };
    }, [ChatId]);
    useEffect(() => {
        console.log('ChatId first', ChatId, typeof ChatId)
        if (ChatId === 'undefined') {
            const timer = setInterval(() => {
                const id = Cookie.getCookie('activeChatId');
                console.log('chat id', id)
                if (id) {
                    setChatId(id);
                    clearInterval(timer);
                }
            }, 100);
            return () => clearInterval(timer);
        }
    }, []);
    // socket listeners
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;
        socket.emit("set_active_chat", { chat_id: ChatId });
        const onSocketConnect = () => {
            console.log("🔄 Reconnected → refetch messages");
            getUserList();
        };

        const onMessage = (data) => {
            // Clear any pending timeouts and queued responses from previous message
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            aiResponseQueueRef.current = null;
            isTypingShowingRef.current = false;
            setAiTyping(false);

            // Calculate "reading" delay based on user message length
            // More realistic timing: higher base for short messages, slower scaling for long ones
            const msgLen = userMessageLengthRef.current;
            const readingDelay = msgLen <= 30
                ? 2000 + msgLen * 50  // Short messages: 2s base + 50ms/char
                : 2000 + 30 * 50 + (msgLen - 30) * 20; // Long messages: slower scaling at 35ms/char

            // Add user message immediately
            // setMessageList(prev => [...prev, data.message]);
            setMessageList(prev => {
                return prev.map(msg => {
                    if (msg.temp_id && msg.temp_id === data.temp_id) {
                        return {
                            ...data.message,
                            status: "sent"
                        };
                    }
                    return msg;
                });
            });

            // Show typing indicator after "reading" delay
            // typingTimeoutRef.current = setTimeout(() => {
            if (!isMounted.current) return;
            isTypingShowingRef.current = true; // Set sync flag FIRST
            setAiTyping(true);
            setTimeout(() => scrollToBottom('smooth'), 30);

            // If AI response arrived early and is queued, process it now
            if (aiResponseQueueRef.current) {
                const queuedData = aiResponseQueueRef.current;
                aiResponseQueueRef.current = null;

                // Calculate typing delay based on realistic typing speed (70 WPM)
                // 70 WPM = 350 CPM = 5.83 CPS = 172ms per character
                const responseLength = queuedData.message?.content?.length || 0;
                // const typingDelay = Math.max(responseLength * 172, 1000);

                // typingTimeoutRef.current = setTimeout(() => {
                if (!isMounted.current) return;
                isTypingShowingRef.current = false;
                setAiTyping(false);
                setMessageList(prev => [...prev, queuedData.message]);
                setTimeout(() => scrollToBottom('smooth'), 30);
                // }, typingDelay);
            }
            // }, readingDelay);
        };

        const onAI = (data) => {
            // const isTypingShowing = isTypingShowingRef.current; // Use sync ref instead of state
            if (data?.usage) {
                setSubscriptionLimit(data?.usage)
            }
            // If typing animation is already showing, proceed immediately
            // if (isTypingShowing) {
            // Calculate typing delay based on realistic typing speed (70 WPM)
            // 70 WPM = 350 CPM = 5.83 CPS = 172ms per character
            const responseLength = data.message?.content?.length || 0;
            const typingDelay = Math.max(responseLength * 172, 1000);

            // typingTimeoutRef.current = setTimeout(() => {
            if (!isMounted.current) return;
            // isTypingShowingRef.current = false;
            setAiTyping(false);
            setMessageList(prev => [...prev, data.message]);
            setTimeout(() => scrollToBottom('smooth'), 30);
            // }, typingDelay);
            // } else {
            //     // Typing animation not showing yet, queue the response
            //     aiResponseQueueRef.current = data;
            // }
        };
        const useLimitfunc = (data) => {
            setTimeout(() => {
                setUseLimit(data)
            }, 3000);
        }
        const handleLimitExceeded = (data) => {
            if (!data?.details?.allowed) {
                if (data?.error_type === 'text_message') {
                    setOpenFrom('paywall')
                    setSubscriptionLimit((prev) => ({
                        ...(prev || {}),
                        user_type: 'unsubscribed',
                        text_messages_remaining: 0,
                    }))
                    getLimitsOfMessages()
                } else if (data?.error_type === 'voice_minutes') {
                    setOpenFrom('voice')
                } else if (data?.error_type === 'video_minutes') {
                    setOpenFrom('video')
                } else {
                    setOpenFrom('chat')
                }
                toggleForModal('TOGGLE_UPGRADE_SUBSCRIPTION')
            }

        }
        const handleShowTour = (data) => {
            const seen = localStorage.getItem('chat_tour');
            if (seen !== 'true') {
                localStorage.setItem('chat_tour', 'true');
                controls.start();
            }
        }
        socket.on("connect", onSocketConnect);
        socket.on("message_received", onMessage);
        socket.on("ai_response", onAI);
        socket.on("usage_limit_warning", useLimitfunc);
        socket.on("usage_limit_exceeded", handleLimitExceeded);
        socket.on("show_tour", handleShowTour);

        return () => {
            socket.off("connect", onSocketConnect);
            socket.off("message_received", onMessage);
            socket.off("ai_response", onAI);
            socket.off("usage_limit_warning", useLimitfunc);
            socket.off("usage_limit_exceeded", handleLimitExceeded);
            socket.off("show_tour", handleShowTour);
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            socket.emit("clear_active_chat");
        };
    }, [isInitialized]);

    // preload audio + navigate to video-call
    const handlePreloadVideo = async () => {
        if (subscriptionLimit?.video_minutes_remaining === 0) {
            setOpenFrom('video')
            toggleForModal('TOGGLE_UPGRADE_SUBSCRIPTION')
        } else {
            await unlockAudioContext();
            preloadAudio(['/sounds/ringing.mp3', '/sounds/pickup.mp3', '/sounds/hangup.mp3']);
            const socket = getSocket();
            if (socket && creatorDetails?.user_id) {
                socket.emit('warm_call_memory', { creator_id: creatorDetails.user_id, mode: 'video' });
            }
            navigator('/user/video-call?id=' + creatorDetails?.user_id + '&name=' + creatorDetails?.first_name + '&video=true');
        }
    };

    // handle audio call
    const handleAudioCall = async () => {

        if (subscriptionLimit?.voice_minutes_remaining === 0) {
            setOpenFrom('voice')
            toggleForModal('TOGGLE_UPGRADE_SUBSCRIPTION')
        } else {
            await unlockAudioContext();
            preloadAudio(['/sounds/ringing2.mp3', '/sounds/pickup.mp3', '/sounds/hangup.mp3']);
            const socket = getSocket();
            if (socket && creatorDetails?.user_id) {
                socket.emit('warm_call_memory', { creator_id: creatorDetails.user_id, mode: 'voice' });
            }
            navigator('/user/video-call?id=' + creatorDetails?.user_id + '&name=' + creatorDetails?.first_name + '&video=false')
        }

    };

    // scroll-to-bottom helper (uses bottomRef and block:'end' so it lands at exact bottom)
    const scrollToBottom = (behavior = 'auto') => {
        try {
            bottomRef.current?.scrollIntoView({ behavior, block: 'end' });
        } catch (e) {
            // fallback: try to set scrollTop of container
            const container = scrollContainerRef.current;
            if (container) container.scrollTop = container.scrollHeight;
        }
    };
    const handleTouchStart = (e) => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const touch = e.touches[0];
        touchStartY.current = touch.clientY;

        const isNearBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight < 40;

        swipeActivated.current = isNearBottom;
    };
    const handleTouchMove = (e) => {
        if (!swipeActivated.current) return;

        const touch = e.touches[0];
        touchEndY.current = touch.clientY;

        const diff = touchStartY.current - touchEndY.current;

        if (diff > 120) {
            activateInvisibleMode();
            swipeActivated.current = false;
        }
    };
    // Compute which date label should float at the top of the chat area.
    // Logic: the last separator whose top edge has scrolled above the floating chip position
    // becomes the active label. If a separator is currently inside the chip's zone (about to
    // collide), hide the chip so the natural separator shows cleanly instead.
    const updateFloatingDate = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        // The floating chip sits at ~8px from the container top
        const CHIP_THRESHOLD = 40; // px — hide chip if a separator is within this range
        const CHIP_ACTIVE_Y = 32;  // px — separator must be at or above this to activate chip

        const entries = Object.entries(dateSepElements.current)
            .map(([label, el]) => {
                if (!el) return null;
                const relTop = el.getBoundingClientRect().top - containerRect.top;
                return { label, relTop };
            })
            .filter(Boolean)
            .sort((a, b) => a.relTop - b.relTop); // top-to-bottom order

        let activeLabel = null;
        let hideChip = false;

        for (const { label, relTop } of entries) {
            if (relTop <= CHIP_ACTIVE_Y) {
                // Separator has scrolled past the chip position — it's the current date
                activeLabel = label;
            }
            // If separator is currently inside the chip collision zone, hide to avoid overlap
            if (relTop >= -4 && relTop <= CHIP_THRESHOLD) {
                hideChip = true;
            }
        }

        const next = hideChip ? null : activeLabel;

        // Imperatively update DOM — avoids React re-renders on every scroll frame
        if (next !== floatingLabelRef.current) {
            floatingLabelRef.current = next;
            const chip = floatingChipRef.current;
            const text = floatingChipTextRef.current;
            if (chip && text) {
                text.textContent = next || '';
                chip.style.opacity = next ? '1' : '0';
                chip.style.transform = next ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(-8px)';
            }
        }
    };

    // handle scroll -> detect top -> load older messages and preserve position
    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.classList.add('is-scrolling');
        clearTimeout(scrollFadeTimerRef.current);
        scrollFadeTimerRef.current = setTimeout(() => {
            container.classList.remove('is-scrolling');
        }, 1000);

        updateFloatingDate();

        // only trigger when scrolled to very top
        if (container.scrollTop <= 0 && !loadingMore && messageList.length > 0) {
            isPaginatingRef.current = true; // mark pagination so auto-scroll doesn't fire
            const prevHeight = container.scrollHeight;
            const beforeCursor = messageList[0]?.created_at;

            getUserList(beforeCursor).then((count) => {
                // maintain visual position after items prepended
                if (count > 0 && container) {
                    const newHeight = container.scrollHeight;
                    const diff = newHeight - prevHeight;
                    // set scrollTop to diff so user stays at same message
                    container.scrollTop = diff;
                    // Recompute chip after layout settles
                    setTimeout(updateFloatingDate, 50);
                }
                // allow auto-scroll for subsequent appended messages
                // add a small delay so messageList effect won't mis-detect
                setTimeout(() => {
                    isPaginatingRef.current = false;
                }, 20);
            }).catch(() => {
                isPaginatingRef.current = false;
            });
        }
    };

    // send message
    const handleEmitMessage = (override) => {
        const text = (typeof override === 'string' ? override : message).trim();
        if (text === '') return toast.error('Please enter a message');
        // Store message length for reading delay calculation
        userMessageLengthRef.current = text.length;

        const temp_id = Date.now();

        const tempMessage = {
            temp_id,
            content: text,
            role: "user",
            status: "sending",
            created_at: new Date().toISOString()
        };

        // 1. Add instantly (UI update)
        setMessageList(prev => [...prev, tempMessage]);

        const socket = getSocket();
        if (!socket) return;
        // 2. Emit with tempId
        socket?.emit('send_message', {
            message: text,
            chat_id: ChatId,
            creator_id: creatorId,
            temp_id
        });

        setMessage('');
    };

    const splashUserId = user?.id || user?.user_id || user?._id;

    const syncSplashShown = async () => {
        if (splashSyncedRef.current) return;
        splashSyncedRef.current = true;
        markSplashShownLocally(splashUserId);
        try {
            const response = await CallWithAuth("PATCH", '/users/me', {
                splash_video_shown: true
            });
            const nextUser = response?.data;
            if (nextUser && (nextUser.user_id || nextUser._id || nextUser.id)) {
                updateUser(nextUser);
            } else {
                splashSyncedRef.current = false;
            }
        } catch {
            splashSyncedRef.current = false;
        }
    };

    const handleAcceptSplashScreen = async () => {
        setSplashApiLoading(true);
        await syncSplashShown();
        setShowOnboarding(false);
        setSplashApiLoading(false);
    };

    useEffect(() => {
        setHistoryLoaded(false);
        if (!ChatId) {
            console.warn("⛔ getUserList skipped — no ChatId");
            return;
        }

        const socket = getSocket();
        if (!socket) {
            console.warn("⛔ getUserList skipped — socket not ready, isInitialized:", isInitialized);
            return;
        }
        if (isInitialized !== 'connected') return;
        console.log("✅ getUserList firing — ChatId:", ChatId, "socket:", !!socket);
        getUserList().then(() => {
            setTimeout(() => {
                scrollToBottom('auto');
                updateFloatingDate();
            }, 80);
        });
    }, [ChatId, isInitialized]);

    // Pin the remember line once per visit. A reload mounts fresh state, so it
    // lands under whatever the latest reply is at load, then stays there.
    useEffect(() => {
        if (rememberChatIdRef.current !== ChatId) {
            rememberChatIdRef.current = ChatId;
            rememberPinnedRef.current = false;
            setRememberAnchorId(null);
            return;
        }
        if (rememberPinnedRef.current || !historyLoaded) return;
        const firstUser = messageList.findIndex((msg) => msg.role === 'user');
        if (firstUser === -1) return;
        for (let i = messageList.length - 1; i > firstUser; i -= 1) {
            if (messageList[i].role !== 'user') {
                rememberPinnedRef.current = true;
                setRememberAnchorId(messageList[i]._id ?? messageList[i].created_at);
                return;
            }
        }
    }, [ChatId, historyLoaded, messageList]);

    useEffect(() => {
        const onClaimed = (event) => {
            const chat = event.detail?.chats?.[0];
            if (chat?.chat_id) {
                const nextId = String(chat.chat_id);
                if (nextId !== String(ChatId || "")) setMessageList([]);
                Cookie.setCookie('activeChatId', chat.chat_id, 1);
                setChatId(nextId);
            }
            if (creatorId) {
                CallWithAuth("GET", GET_CREATOR_DETAILS(creatorId)).then((response) => {
                    if (response?.data) setUserDetails(response.data);
                });
                CallWithAuth("GET", GET_MESSAGE_LIMITS(creatorId)).then((response) => {
                    if (response?.data) setSubscriptionLimit(response.data);
                });
            }
            setShowAccountPrompt(false);
        };
        window.addEventListener('tlk:trial-claimed', onClaimed);
        return () => window.removeEventListener('tlk:trial-claimed', onClaimed);
    }, [creatorId, ChatId]);

    // auto-scroll logic:
    // only scroll when last message is appended (id changes) and not during pagination
    useEffect(() => {
        if (messageList.length === 0) {
            lastMessageIdRef.current = null;
            return;
        }

        const lastMessage = messageList[messageList.length - 1];
        const lastId = lastMessage?.id ?? lastMessage?.created_at ?? JSON.stringify(lastMessage);

        // if paginating (loading older) -> do nothing
        if (isPaginatingRef.current) {
            lastMessageIdRef.current = lastId; // update reference anyway
            return;
        }

        // If last message id changed => appended new message -> scroll to bottom
        if (lastMessageIdRef.current !== lastId) {
            // slight delay to allow new node to render
            setTimeout(() => {
                scrollToBottom('smooth');
            }, 30);
        }

        lastMessageIdRef.current = lastId;
    }, [messageList]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            clearTimeout(scrollFadeTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const root = chatRootRef.current;
        const vv = window.visualViewport;
        if (!root || !vv) return;

        const syncViewport = () => {
            root.style.height = `${vv.height}px`;
            root.style.top = `${vv.offsetTop}px`;
        };

        syncViewport();
        // Only resize: listening to visualViewport "scroll" moves this fixed shell whenever
        // the browser adjusts the visual viewport (often during inner-area scroll / overscroll),
        // which feels like inverted or "whole page" scrolling.
        vv.addEventListener("resize", syncViewport);
        return () => {
            vv.removeEventListener("resize", syncViewport);
            root.style.height = "";
            root.style.top = "";
        };
    }, []);

    // Handle focus to scroll to bottom when returning from call
    useEffect(() => {
        const handleFocus = () => {
            setTimeout(() => scrollToBottom('smooth'), 100);
            refreshUser();
            if (creatorId) getLimitsOfMessages();
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [creatorId]);
    useEffect(() => {
        const currentUrl = window.location.href;
        // Push two entries so Safari always has a buffer to pop back to
        window.history.pushState(null, null, currentUrl);
        window.history.pushState(null, null, currentUrl);

        let timer;
        const handlePopState = () => {
            window.history.pushState(null, null, currentUrl);
            navigator(getAnonToken() && creatorUsername ? `/${creatorUsername}` : '/user/creator-list');
        };

        // Defer listener so Safari's initial popstate on load doesn't trigger it
        timer = setTimeout(() => {
            window.addEventListener('popstate', handlePopState);
        }, 100);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);
    const userType = subscriptionLimit?.user_type;
    const fanUserId = user?.user_id || user?._id;
    useEffect(() => {
        if (isPwaInstalled()) return;
        // if (!user?.splash_video_shown) return;
        // if (showOnboarding) return;
        if (!user) return;
        if (!userType || userType === 'unsubscribed') return;
        if (isPwaTipDismissed(fanUserId) && !showPwaTipParam) return;

        setShowPwaInstallTip(true);
        const interval = Number(process.env.REACT_APP_PWA_MODAL_FREQUENCY) || 30000;
        const pwaIntervalId = setInterval(() => {
            if (isPwaInstalled()) return;
            if (isPwaTipDismissed(fanUserId) && !showPwaTipParam) return;
            setShowPwaInstallTip(true);
        }, interval);

        return () => {
            clearInterval(pwaIntervalId);
        };
    }, [fanUserId, userType, showPwaTipParam]);

    const checkOnboarding = useCallback(() => {
        if (!user) return;

        // creatorDetails starts as {}. The paywall auto-opens on
        // `onboardingChecked && !showOnboarding`, so we must NOT mark onboarding
        // checked until the creator API has actually loaded — otherwise the
        // paywall opens before we know whether splash should play.
        const creatorReady = Boolean(
            creatorDetails?.user_id || creatorDetails?.id || creatorDetails?._id
        );
        if (!creatorReady) return;

        const hasSplashVideo = Boolean(
            creatorDetails?.is_creator && creatorDetails?.splash_video_url
        );
        const inStandalonePwa = isPwaInstalled();
        // Source of truth is the server flag; local per-user key is a fallback.
        const alreadyShown =
            Boolean(user.splash_video_shown) || isSplashShownLocally(splashUserId);

        // Show splash once, in the browser, before the paywall. Never in the
        // installed PWA (the user already signed up before installing).
        const shouldShowSplash = hasSplashVideo && !alreadyShown && !inStandalonePwa;

        // setShowOnboarding(shouldShowSplash);

        // If we're skipping a splash the server still thinks is unseen (installed
        // PWA, or a stale/failed earlier write), record it so it can't resurface
        // in a later browser session.
        // if (hasSplashVideo && !shouldShowSplash && !user.splash_video_shown) {
        //     void syncSplashShown();
        // }

        // setOnboardingChecked(true);
    }, [creatorDetails, user]);

    useEffect(() => {
        checkOnboarding();
    }, [checkOnboarding]);
    const getPlatform = () => {
        const ua = navigator.userAgent;
        if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
        if (/Android/.test(ua)) return 'android';
        return 'other';
    };
    const chatStartedRef = useRef('');
    const rememberTrackedRef = useRef(false);
    const trialEndTrackedRef = useRef(false);
    useEffect(() => {
        if (!historyLoaded || !ChatId) return;
        if (chatStartedRef.current === String(ChatId)) return;
        chatStartedRef.current = String(ChatId);
        trackEvent('chat_started', { chat_id: ChatId, username: creatorUsername || undefined });
    }, [historyLoaded, ChatId, creatorUsername]);

    const showStarters = historyLoaded
        && starterSuggestions.length > 0
        && !messageList.some((msg) => msg.role === 'user');
    const trialEnded = isAnonymous && subscriptionLimit?.user_type === 'unsubscribed';
    const showRemember = isAnonymous && subscriptionLimit?.user_type === 'trial' && !trialEnded
        && rememberAnchorId != null;
    useEffect(() => {
        if (!showRemember || rememberTrackedRef.current) return;
        rememberTrackedRef.current = true;
        trackEvent('registration_prompt_shown', { surface: 'remember', username: creatorUsername || undefined });
    }, [showRemember, creatorUsername]);
    useEffect(() => {
        if (!trialEnded || trialEndTrackedRef.current) return;
        trialEndTrackedRef.current = true;
        trackEvent('registration_prompt_shown', { surface: 'trial_end', username: creatorUsername || undefined });
    }, [trialEnded, creatorUsername]);
    const accountPath = `/user/chat?id=${encodeURIComponent(creatorId || '')}&username=${encodeURIComponent(creatorUsername)}`;
    return (
        <>
            <Helmet><title>{`${creatorDetails.first_name ?? ""}  ${creatorDetails.last_name ?? ""}`} | TLK</title></Helmet>
            {/* <PayrollModal /> */}
            {/* {TOGGLE_UPGRADE_SUBSCRIPTION && */}

            <UpgradeSubscriptionModal
                creatorId={creatorId}
                creatorDetails={creatorDetails}
                subscriptionLimit={subscriptionLimit}
                openFrom={openFrom}
                canAutoOpen={onboardingChecked && !isAnonymous}
            />

            {Tour}

            <PwaInstallTip
                open={showPwaInstallTip}
                onClose={() => setShowPwaInstallTip(false)}
                onInstalled={() => {
                    dismissPwaTip(fanUserId);
                    setShowPwaInstallTip(false);
                }}
                platform={getInstallPlatform()}
            />

            {/* ============== start: chat listing page ============== */}
            <div className="chatpage-main-div chat-page" ref={chatRootRef}>
                {(trialEnded || showAccountPrompt) && (
                    <div className="chat-login-wall">
                        <div className="chat-login-sheet">
                        {!trialEnded && (
                            <button type="button" className="chat-login-close" onClick={() => setShowAccountPrompt(false)} aria-label="Close">
                                ×
                            </button>
                        )}
                        <SimplifiedAuthCard
                            initialMode="signup"
                            afterAuthPath={accountPath}
                            showLogo={false}
                            introText={trialEnded
                                ? "Sign in to keep this conversation, then choose a plan."
                                : `Sign in to make sure your conversation with ${creatorDetails?.first_name || "them"} stays with you.`}
                            submitLabel="Continue"
                        />
                        </div>
                    </div>
                )}
                {/* <VideoModal
                    open={showOnboarding}
                    onClose={() => handleAcceptSplashScreen()}
                    videoSrc={creatorDetails?.splash_video_url}
                    ctaLabel={splashApiLoading ? "Loading..." : "Continue"}
                    onComplete={() => console.log('done')}
                /> */}
                {/* Connection failed overlay */}
                {(isInitialized === 'failed' || isInitialized === 'timeout') && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 100,
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                            backgroundColor: 'rgba(0,0,0,0.55)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '16px',
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
                            fill="none" stroke="#FF4DA6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="1" y1="1" x2="23" y2="23" />
                            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                            <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
                            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                            <line x1="12" y1="20" x2="12.01" y2="20" />
                        </svg>
                        <p style={{ color: '#fff', fontSize: '15px', fontWeight: 500, margin: 0 }}>
                            Connection failed
                        </p>
                        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px', margin: 0 }}>
                            The conversation was interrupted. Reload to pick up where you left off
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                marginTop: '4px',
                                background: '#FF4DA6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '999px',
                                padding: '10px 28px',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                            }}
                        >
                            Reload
                        </button>
                    </div>
                )}
                {isInitialized === 'connecting' && (
                    <div className="relative w-full h-8 bg-gray-700/80 backdrop-blur-sm w-full absolute top-0 left-0 z-50">

                        <div className="relative w-full h-8 bg-gray-700 overflow-hidden flex items-center justify-center">

                            {/* Animated loader */}
                            <div className="absolute left-0 top-0 h-full w-1/3 bg-gradient-to-r from-[#AF0056] via-pink-500 to-[#FF4D9D] animate-[loading_1.2s_linear_infinite]" />

                            {/* Centered text */}
                            <span className="relative z-10 text-xs text-white font-medium">
                                Connecting, please wait...
                            </span>

                        </div>

                    </div>
                )}
                <div className="message-header chat-page-header px-4 py-3 flex items-center justify-between border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <button className="text-chat-text" onClick={() => navigator(isAnonymous && creatorUsername ? `/${creatorUsername}` : '/user/creator-list')}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#fff"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-arrow-left w-6 h-6"
                            >
                                <path d="m12 19-7-7 7-7" />
                                <path d="M19 12H5" />
                            </svg>
                        </button>
                        {/* <img
                                    src="/images/reya-avatar-DwVTKqWN.png"
                                    alt="Reya"
                                    className="w-10 h-10 rounded-full border-2 border-primary"
                                /> */}
                        <button
                            type="button"
                            className="chat-creator-summary flex items-center gap-3 text-left"
                            onClick={() => navigator(`/user/creator-profile?id=${creatorId}`)}
                        >
                            <Avatar alt="User" src={creatorDetails?.profile_picture_url}
                                className="w-10 h-10 rounded-full border-2 border-primary"
                            >
                                {creatorDetails?.first_name?.charAt(0)}
                            </Avatar>
                            <div>
                                <h2 className="font-semibold text-chat-text">
                                    {creatorDetails?.first_name
                                        ? <span className="name-fade-in" style={{ color: '#fff' }}>{`${creatorDetails.first_name}  ${creatorDetails.last_name ?? ""}`}</span>
                                        : <span className="name-skeleton" />}
                                </h2>
                                <p className="text-xs text-chat-textMuted">Online</p>
                            </div>
                        </button>
                    </div>
                    <div className="flex items-center gap-4">
                        {(!loaderLimit && subscriptionLimit?.text_messages_remaining != null) && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (isAnonymous) { setShowAccountPrompt(true); return; }
                                    toggleForModal('TOGGLE_UPGRADE_SUBSCRIPTION');
                                }}
                                className="flex flex-col items-center gap-0.5 text-chat-text hover:text-primary transition-colors"
                            >
                                <IoChatbubbleOutline size={20} />
                                <span className="text-[10px] leading-none text-chat-textMuted font-medium whitespace-nowrap">
                                    {formatMessageCount(subscriptionLimit?.text_messages_remaining)} left
                                </span>
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={isInitialized === 'connecting' ? true : false}
                            className="flex flex-col items-center gap-0.5 text-chat-text hover:text-primary transition-colors"
                            data-step="1"
                            onClick={() => {
                                if (isAnonymous) { setShowAccountPrompt(true); return; }
                                handlePreloadVideo();
                            }}
                        >
                            <span className="relative inline-flex">
                                <IoVideocamOutline size={20} />
                                <img src="/images/beta-branding.svg" alt="" className="absolute -top-2.5 -right-3.5 w-6 pointer-events-none" />
                            </span>
                            {(!loaderLimit && subscriptionLimit?.user_type !== 'trial' && subscriptionLimit?.video_minutes_limit != null) && (
                                <span className="text-[10px] leading-none text-chat-textMuted font-medium whitespace-nowrap">
                                    {formatMinutesToClock(subscriptionLimit?.video_minutes_remaining)}
                                </span>
                            )}
                        </button>
                        <button
                            type="button"
                            className="flex flex-col items-center gap-0.5 text-chat-text hover:text-primary transition-colors"
                            data-step="2"
                            disabled={isInitialized === 'connecting' ? true : false}
                            onClick={() => {
                                if (isAnonymous) { setShowAccountPrompt(true); return; }
                                handleAudioCall();
                            }}
                        >
                            <IoCallOutline size={20} />
                            {(!loaderLimit && subscriptionLimit?.user_type !== 'trial' && subscriptionLimit?.voice_minutes_limit != null) && (
                                <span className="text-[10px] leading-none text-chat-textMuted font-medium whitespace-nowrap">
                                    {formatMinutesToClock(subscriptionLimit?.voice_minutes_remaining)}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Message area ── */}
                <div className="chat-page-body w-full relative message-body">
                <div
                    className="chat-thread-scroll custom-scrollbar"
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                >
                    {/* ── Floating date chip (WhatsApp-style) ──
                                 Natural-height sticky + negative margin-bottom = zero layout impact.
                                 All visual updates are imperative (no re-renders on scroll). */}
                    <div
                        style={{
                            position: 'sticky',
                            top: 8,
                            zIndex: 20,
                            display: 'flex',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            marginBottom: '-36px',
                        }}
                    >
                        <div
                            ref={floatingChipRef}
                            style={{
                                opacity: 0,
                                transform: 'scale(0.85) translateY(-8px)',
                                transition: 'opacity 250ms cubic-bezier(0.34, 1.56, 0.64, 1), transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                willChange: 'opacity, transform',
                            }}
                            className="max-w-[80%] rounded-2xl px-2 py-1 bg-chat-creatorBubble 
                                    text-chat-text shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
                        >
                            <p ref={floatingChipTextRef} className="text-xs leading-relaxed text-white" />
                        </div>

                    </div>

                    <div className="flex flex-col gap-y-4">
                        {/* Load-more spinner instead of plain "Loading..." text */}
                        {loadingMore && (
                            <div className="flex justify-center py-2">
                                <div className="loaderBox" style={{ position: 'unset', margin: 0, padding: 0, height: 'unset', width: 'unset' }}>
                                    <div className="loader" style={{ width: 24, height: 24 }} />
                                </div>
                            </div>
                        )}

                        {messageList.map((msg, idx) => {
                            const currentDateLabel = getDateLabel(msg.created_at);
                            const prevDateLabel = idx > 0 ? getDateLabel(messageList[idx - 1].created_at) : null;
                            const showDateSeparator = currentDateLabel !== prevDateLabel;

                            return (
                                <React.Fragment key={msg?._id}>
                                    {showDateSeparator && (
                                        <div
                                            ref={el => {
                                                if (el) dateSepElements.current[currentDateLabel] = el;
                                                else delete dateSepElements.current[currentDateLabel];
                                            }}
                                            className="max-w-[80%] rounded-2xl px-2 py-1 bg-chat-creatorBubble text-chat-text mx-auto my-2 shadow-[0_2px_8px_rgba(0,0,0,0.4)] visible"
                                        >
                                            <p className="text-xs leading-relaxed text-white">
                                                {currentDateLabel}
                                            </p>
                                        </div>
                                    )}
                                    <motion.div
                                        layout
                                        animate={{
                                            paddingRight: msg.status === "sending" && msg.role === 'user' ? 32 : 0
                                        }}
                                        transition={{
                                            paddingRight: {
                                                duration: 0.28,
                                                ease: "easeInOut"
                                            },
                                            layout: {
                                                duration: 0.28,
                                                ease: "easeInOut"
                                            }
                                        }}
                                        className={`flex flex-col invisible msg-enter 
                                                    ${msg.role === 'user' ? "items-end" : "items-start"}`}

                                        onTouchStart={(e) => startLongPress(e, msg)}
                                        onTouchEnd={endLongPress}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            startLongPress(e, msg);
                                        }}
                                        onMouseUp={endLongPress}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            startLongPress(e, msg);
                                        }}
                                    >

                                        <div className={`flex visible items-center gap-2 mb-1 ${msg.role === 'user' ? "flex-row-reverse" : "flex-row"}`}>
                                            {msg.role !== 'user' && (
                                                <Avatar alt="User" src={creatorDetails?.profile_picture_url}
                                                    className="w-6 h-6 rounded-full"
                                                    sx={{ width: 28, height: 28, fontSize: 14 }}
                                                >
                                                    {creatorDetails?.first_name?.charAt(0)}
                                                </Avatar>
                                            )}
                                            <span className="text-xs text-chat-textMuted uppercase tracking-wide font-medium">
                                                {msg.role === 'user' ? 'YOU' : creatorDetails?.first_name}
                                            </span>
                                            <span className="text-xs text-chat-textMuted">{utcToLocal(msg.created_at)}</span>
                                        </div>

                                        <div className={`flex items-end gap-2 w-full ${msg.role === 'user' && "justify-end"}`}>
                                            <motion.div
                                                animate={
                                                    reactionMsg?._id === msg._id
                                                        ? { scale: 0.95, y: -2 }
                                                        : (msg.status === "sending" && msg.role === 'user')
                                                            ? {
                                                                scale: [1, 1.02, 1],
                                                                opacity: [0.8, 1, 0.8],
                                                                y: [0, -1, 0]
                                                            }
                                                            : { scale: 1, y: 0, opacity: 1 }
                                                }
                                                transition={{
                                                    ...(msg.status === "sending" && msg.role === 'user' && reactionMsg?._id !== msg._id
                                                        ? {
                                                            duration: 1.1,
                                                            repeat: Infinity,
                                                            ease: "easeInOut"
                                                        }
                                                        : {
                                                            type: "spring",
                                                            stiffness: 250,
                                                            damping: 18
                                                        })
                                                }}
                                                className={`chat-message max-w-[80%] visible relative rounded-2xl px-4 py-3 bg-chat-creatorBubble text-chat-text
                                                 no-select ${msg.role === 'user' ? 'chat-message-user' : 'chat-message-creator border-2 border-primary !bg-[#22222a]'}
                                                      
                                                      `}
                                            >
                                                <p className="text-sm leading-relaxed text-white text-wrap-force">
                                                    {msg.content}
                                                    {/* {msg.role === "user" && (
                                                            <span className="text-xs opacity-70">
                                                                {msg.status === "sending" && "🕓"}
                                                                {msg.status === "sent" && <DoneAllRoundedIcon sx={{ fontSize: 16 }} />}
                                                            </span>
                                                        )} */}
                                                </p>
                                                {(() => {
                                                    const msgId = msg._id ?? msg.created_at;
                                                    const reaction = messageReactions[msgId];
                                                    return reaction ? (
                                                        <div className='absolute w-full left-0 -bottom-[10px] flex cursor-pointer'
                                                            onClick={() => {
                                                                setMessageReactions(prev => ({ ...prev, [msgId]: null }))
                                                            }}
                                                        >
                                                            <motion.span
                                                                key={reaction}
                                                                initial={{ scale: 0, opacity: 0 }}
                                                                animate={{ scale: 1, opacity: 1 }}
                                                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                                                className={`text-base flex bg-[#1c1c22] rounded-lg border !border-[#ffffff1a] px-1 ${msg.role !== 'user' ? 'ml-auto' : ''}`}
                                                            >
                                                                {reaction}
                                                            </motion.span>
                                                        </div>
                                                    ) : null;
                                                })()}
                                            </motion.div>
                                            {(msg.status === "sending" && msg.role === 'user') && (
                                                <motion.div
                                                    className="visible svg-path-white"
                                                    animate={{ opacity: [0.35, 1, 0.35], scale: [0.9, 1.15, 0.9] }}
                                                    transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
                                                >
                                                    <GoDotFill />
                                                </motion.div>
                                            )}
                                        </div>

                                        {/* <div className="max-w-[75%]">
                                                        <div className="relative rounded-2xl overflow-hidden shadow-lg">
                                                            <img
                                                                src="/images/reya-avatar-DwVTKqWN.png"
                                                                alt="Reya"
                                                                className="w-full h-auto"
                                                            />
                                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                                                                <div className="flex items-center gap-1 text-white/90 text-xs">
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width={24}
                                                                        height={24}
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="#fff"
                                                                        strokeWidth={2}
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        className="lucide lucide-sparkles w-3 h-3"
                                                                    >
                                                                        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                                                                        <path d="M20 3v4" />
                                                                        <path d="M22 5h-4" />
                                                                        <path d="M4 17v2" />
                                                                        <path d="M5 18H3" />
                                                                    </svg>
                                                                    <span className="italic text-white/90 text-white/90">Early selfie (messages 2-4)</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div> */}
                                        {showRemember && (msg._id ?? msg.created_at) === rememberAnchorId && (
                                            <div
                                                className="chat-remember visible"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                            >
                                                <button type="button" className="chat-remember-copy" onClick={() => setShowAccountPrompt(true)}>
                                                    Want me to remember this chat or already have an account? <span>Sign in</span>
                                                </button>
                                            </div>
                                        )}
                                    </motion.div>

                                    {
                                        (useLimit?.details?.percentage_used == 80 && idx == messageList.length - 1) ?
                                            <div className="visible flex justify-center" style={{ opacity: 1, transform: "none" }}>
                                                <div className="flex flex-col items-start max-w-[85%]">
                                                    <div
                                                        className="w-full bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl p-4 shadow-lg border !border-[#ff4da6]"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className="bg-[#6f335a] rounded-full p-2 flex-shrink-0">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width={24}
                                                                    height={24}
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="#ff4da6"
                                                                    strokeWidth={2}
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    className="lucide lucide-sparkles w-4 h-4 text-primary"
                                                                >
                                                                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                                                                    <path d="M20 3v4" />
                                                                    <path d="M22 5h-4" />
                                                                    <path d="M4 17v2" />
                                                                    <path d="M5 18H3" />
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-chat-text mb-1">
                                                                    You and {creatorDetails?.first_name} are really hitting it off... When your free messages
                                                                    end, you can unlock a plan to keep things going.
                                                                </p>
                                                                <button className="text-xs text-primary font-semibold hover:underline"
                                                                    onClick={() => {
                                                                        setOpenFrom('percentage')
                                                                        toggleForModal('TOGGLE_UPGRADE_SUBSCRIPTION')
                                                                    }}
                                                                >
                                                                    View Plans →
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div> : null}


                                </React.Fragment>
                            )
                        })}
                        {aiTyping && (
                            <div className="chat-item msg-enter">
                                <div className="chat-contents">
                                    <div className="flex justify-start">
                                        <div className="bg-[#f4e1e8] rounded-full px-3 h-[32px] flex items-center justify-center">
                                            <div className="flex gap-1 typeing-bounce">
                                                <span
                                                    className="w-2 h-2 bg-[#8c737b] rounded-full bounce"

                                                />
                                                <span
                                                    className="w-2 h-2 bg-[#8c737b] rounded-full bounce"

                                                />
                                                <span
                                                    className="w-2 h-2 bg-[#8c737b] rounded-full bounce"

                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}

                        {showStarters && (
                            <div className="chat-starters" role="group" aria-label="Suggested messages">
                                {starterSuggestions.map((suggestion) => (
                                    <button
                                        key={suggestion}
                                        type="button"
                                        className="chat-starter"
                                        onClick={() => handleEmitMessage(suggestion)}
                                    >
                                        <svg className="chat-starter-mark" viewBox="0 0 24 24" aria-hidden="true">
                                            <path fill="currentColor" d="M12 1.5l1.35 6.15L19.5 9l-6.15 1.35L12 16.5l-1.35-6.15L4.5 9l6.15-1.35L12 1.5z" />
                                            <path fill="currentColor" d="M18.2 14.2l.55 2.25 2.25.55-2.25.55-.55 2.25-.55-2.25-2.25-.55 2.25-.55.55-2.25z" />
                                        </svg>
                                        <span>{suggestion}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* bottom anchor MUST be inside chat-lists */}
                        <div ref={bottomRef} style={{ height: 1 }} />
                    </div>
                    {invisibleMode && <div className="chatBox-more-loader">
                        <div className="loaderBox"><div className='loader'></div></div>
                        <div className="loderBox-text">Swipe up to refresh messages</div>
                    </div>}
                </div>
                </div>
                <AnimatePresence>
                    {reactionMsg && (
                        <>
                            <motion.div
                                initial={{ scale: 0.6, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.6, opacity: 0, y: 20 }}
                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                className={`fixed z-50 flex flex-col gap-y-4 items-start invisible reactionMsg-main-div ${reactionMsg?.role === 'user' ? 'dir-right' : ''}`}
                                style={{
                                    top: reactionPosition.y - 70,
                                    left: reactionPosition.x - 120
                                }}

                            >
                                <div className="bg-[#1f1f1f] rounded-full px-3 py-2 flex gap-2 shadow-xl visible">
                                    {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((emoji) => (
                                        <motion.span
                                            key={emoji}
                                            whileHover={{ scale: 1.4 }}
                                            whileTap={{ scale: 1.2 }}
                                            onClick={() => {
                                                console.log("Reacted:", emoji, reactionMsg);
                                                const msgId = reactionMsg?._id ?? reactionMsg?.created_at;
                                                setMessageReactions(prev => ({ ...prev, [msgId]: emoji }));
                                                setReactionMsg(null);
                                            }}
                                            className="text-lg cursor-pointer"
                                        >
                                            {emoji}
                                        </motion.span>
                                    ))}
                                    {/* ➕ BUTTON */}
                                    <motion.span
                                        whileHover={{ scale: 1.3 }}
                                        whileTap={{ scale: 1.1 }}
                                        onClick={() => setShowEmojiPicker(true)}
                                        className="text-lg cursor-pointer flex items-center justify-center w-6 h-6 rounded-full bg-[#2a2a2a] text-white"
                                    >
                                        <AddIcon sx={{ fill: "#ffffff !important", fontSize: 18 }} />
                                    </motion.span>
                                </div>
                                {showEmojiPicker && (
                                    <>
                                        {/* Overlay (blur background like WhatsApp) */}
                                        <motion.div
                                            className="fixed inset-0 bg-black/40 z-40 visible"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            onClick={() => setShowEmojiPicker(false)}
                                        />

                                        {/* Bottom Sheet */}
                                        <motion.div
                                            drag="y"
                                            dragConstraints={{ top: 0 }}
                                            dragElastic={0.2}

                                            initial={{ y: "100%" }}
                                            animate={{ y: 0 }}
                                            exit={{ y: "100%" }}

                                            transition={{
                                                type: "spring",
                                                stiffness: 180,
                                                damping: 22
                                            }}

                                            onDragEnd={(e, info) => {
                                                const sheetHeight = window.innerHeight * 0.4; // approx picker height (adjust if needed)
                                                const dragDistance = info.offset.y;

                                                // ✅ 20% threshold
                                                if (dragDistance > sheetHeight * 0.2) {
                                                    setShowEmojiPicker(false);
                                                }
                                            }}
                                            className="fixed bottom-0 left-0 w-full z-50 bg-[#1c1c22] rounded-t-2xl shadow-xl p-3 visible"
                                        >
                                            {/* Drag Handle */}
                                            <div className="w-10 h-1.5 bg-gray-500 rounded-full mx-auto mb-3 cursor-pointer" />

                                            {/* Emoji Picker */}
                                            <div className="h-[300px] overflow-hidden">
                                                <EmojiPicker
                                                    onEmojiClick={(emojiData) => {
                                                        const msgId = reactionMsg?._id ?? reactionMsg?.created_at;
                                                        setMessageReactions(prev => ({ ...prev, [msgId]: emojiData.emoji }));
                                                        setReactionMsg(null);
                                                        setShowEmojiPicker(false);

                                                    }}
                                                    theme="dark"
                                                    width="100%"
                                                    height="100%"
                                                />
                                            </div>
                                        </motion.div>
                                    </>
                                )}
                                <div className={`max-w-[320px] rounded-2xl px-4 py-3 
                                            bg-chat-creatorBubble text-chat-text visible
                                            border-2 border-primary !bg-[#22222a] no-select`}
                                >
                                    <p className="text-sm leading-relaxed text-white">
                                        {reactionMsg?.content}
                                    </p>
                                </div>

                            </motion.div>
                        </>
                    )}

                    {reactionMsg && (
                        <motion.div
                            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 no-select"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => { setReactionMsg(null); setShowEmojiPicker(false); }}
                        />
                    )}

                </AnimatePresence>
                <div className="chat-page-footer border-t border-white/10 p-4 mt-auto message-footer">
                    <div className="chat-composer flex items-center gap-3 bg-chat-inputBg rounded-full px-4 py-3 border-2 border-primary">
                        <div className="text-chat-textMuted hover:text-chat-text transition-colors">
                            {/* <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#fff"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-paperclip w-5 h-5"
                            >
                                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                            </svg> */}
                        </div>
                        <input
                            type="text"
                            placeholder="Type Your Message..."
                            className="w-[calc(100%_-_72px)] bg-transparent outline-none text-chat-text placeholder:text-chat-textMuted"
                            defaultValue=""
                            value={message}
                            // disabled={isInitialized !== 'connected'}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleEmitMessage()}
                        />
                        <button
                            disabled=""
                            className="text-chat-text disabled:text-chat-textMuted transition-colors"
                            onClick={handleEmitMessage}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#fff"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-send w-5 h-5"
                            >
                                <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                                <path d="m21.854 2.147-10.94 10.939" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            {/* ============== end: chat listing page ============== */}

        </>
    );
};

export default Chat;
